import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { SidePanelWrapper } from './styles';

const TRANSITION_MS = 250;
const SLIDE_TRANSITION = `transform ${TRANSITION_MS}ms ease-out`;

interface TemplateListSidePanelProps {
  isOpen: boolean;
  'aria-label'?: string;
  children: ReactNode;
}

export function TemplateListSidePanel({ isOpen, children, 'aria-label': ariaLabel }: TemplateListSidePanelProps) {
  const panelRef = useRef<HTMLElement>(null);
  const [showContent, setShowContent] = useState(false);

  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel) {
      return;
    }

    if (isOpen) {
      setShowContent(false);
      panel.style.transition = 'none';
      panel.style.transform = 'translateX(100%)';
      panel.getBoundingClientRect();

      let revealed = false;
      const revealContent = () => {
        if (revealed) {
          return;
        }
        revealed = true;
        setShowContent(true);
      };

      const onTransitionEnd = (event: TransitionEvent) => {
        if (event.propertyName === 'transform') {
          revealContent();
        }
      };

      const fallbackId = window.setTimeout(revealContent, TRANSITION_MS + 50);
      const rafId = requestAnimationFrame(() => {
        panel.addEventListener('transitionend', onTransitionEnd, { once: true });
        panel.style.transition = SLIDE_TRANSITION;
        panel.style.transform = 'translateX(0)';
      });

      return () => {
        cancelAnimationFrame(rafId);
        window.clearTimeout(fallbackId);
        panel.removeEventListener('transitionend', onTransitionEnd);
      };
    }

    setShowContent(false);
    panel.style.transition = SLIDE_TRANSITION;
    panel.style.transform = 'translateX(100%)';
  }, [isOpen]);

  return (
    <SidePanelWrapper
      ref={panelRef}
      style={{ pointerEvents: isOpen ? 'auto' : 'none' }}
      aria-label={ariaLabel}
      aria-hidden={!isOpen}
    >
      {showContent && children}
    </SidePanelWrapper>
  );
}
